import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DOKUMENTTYPE_BESTEMMELSER,
    finnFrakoblingBlokkering,
    plandokumentInputSchema,
    useArealplan,
    useCreatePlandokument,
    useDeletePlandokument,
    useKnyttPlandokumenterTilBehandling,
    useFrakoblePlandokumentFraBehandling,
} from '@origo/planregister';
import type { FrakoblingBlokkering, Plandokument } from '@origo/planregister';
import { useToast } from '@origo/origo-components';
import type { NewDocumentState } from '../DocumentsPage';
import type { DocumentFieldErrors } from '../components/DocumentForm';
import { emptyDocumentForm, toPlandokumentInput } from '../../../mappers/toDocuments';
import type { DocumentFormValues, DocumentItem } from '../../../types';

/** Feltnære feil fra pakkens zod-schema (jf. ProcessingsContainer). */
function collectFieldErrors(input: unknown): DocumentFieldErrors | null {
    const parsed = plandokumentInputSchema.safeParse(input);
    if (parsed.success) return null;
    const next: DocumentFieldErrors = {};
    for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof DocumentFormValues | undefined;
        if (key && !next[key]) next[key] = issue.message;
    }
    return next;
}

interface UseDocumentManagementParams {
    /** Kortfattede visningsobjekter for dokumentene i gjeldende scope (brukes for slette-oppslag). */
    items: DocumentItem[];
    /** Rådata bak `items` (brukes for å slå opp `objectId` ved sletting). */
    data: Plandokument[] | undefined;
    napId: string;
    /** Når satt: nye dokumenter stemples også med denne behandlingens UID (jf. spec F3.1.2). */
    planbehUid?: string;
    /** Når satt: nye dokumenter stemples også med denne dispensasjonens ID (jf. spec F12.4). */
    dispensasjonId?: string;
}

/** Delt nytt/slett-håndtering for plandokumenter, uavhengig av om visningen er scopet
 *  til hele planen, én planbehandling (`planbehUid`) eller én dispensasjon (`dispensasjonId`). */
export function useDocumentManagement({ items, data, napId, planbehUid, dispensasjonId }: UseDocumentManagementParams) {
    const { t } = useTranslation();
    const toast = useToast();
    const create = useCreatePlandokument();
    const remove = useDeletePlandokument();
    const link = useKnyttPlandokumenterTilBehandling();
    const unlink = useFrakoblePlandokumentFraBehandling();
    const { data: plan, isLoading: planLaster } = useArealplan(napId);

    // --- nytt dokument (F3.1) ---
    const [newOpen, setNewOpen] = useState(false);
    const [newDraft, setNewDraft] = useState<DocumentFormValues>(() => emptyDocumentForm());
    const [newFile, setNewFile] = useState<File | null>(null);
    const [newErrors, setNewErrors] = useState<DocumentFieldErrors>({});
    const [fileError, setFileError] = useState<string | undefined>(undefined);

    const openNew = useCallback(() => {
        setNewDraft(emptyDocumentForm());
        setNewFile(null);
        setNewErrors({});
        setFileError(undefined);
        setNewOpen(true);
    }, []);

    const closeNew = useCallback(() => setNewOpen(false), []);

    const changeNew = useCallback((field: keyof DocumentFormValues, value: string) => {
        setNewDraft(d => ({ ...d, [field]: value }));
        setNewErrors(e => (e[field] ? { ...e, [field]: undefined } : e));
    }, []);

    const changeNewFile = useCallback((file: File | null) => {
        setNewFile(file);
        setFileError(undefined);
    }, []);

    const saveNew = useCallback(async () => {
        const input = toPlandokumentInput(newDraft, napId, { planbehUid, dispensasjonId });
        const errors = collectFieldErrors(input);
        const missingFile = !newFile;
        setNewErrors(errors ?? {});
        setFileError(missingFile ? t('documents.new.fileRequired') : undefined);
        if (errors || missingFile) return;

        const created = await toast.execute(() => create.mutateAsync({ input, file: newFile ?? undefined }), {
            success: t('documents.uploaded'),
        });
        if (created) {
            setNewOpen(false);
            setNewDraft(emptyDocumentForm());
            setNewFile(null);
        }
    }, [newDraft, newFile, napId, planbehUid, dispensasjonId, toast, create, t]);

    // --- knytt eksisterende dokumenter til behandlingen ---
    const [linkExistingOpen, setLinkExistingOpen] = useState(false);
    const openLinkExisting = useCallback(() => setLinkExistingOpen(true), []);
    const closeLinkExisting = useCallback(() => setLinkExistingOpen(false), []);

    // Sheeten forblir montert mellom behandlinger: ikke ta med en åpen dialog
    // eller et ventende «Fjern» videre når behandlingen byttes eller lukkes.
    useEffect(() => {
        setLinkExistingOpen(false);
        setPendingUnlinkId(null);
    }, [planbehUid]);

    const confirmLinkExisting = useCallback(
        async (dokumenter: Plandokument[]) => {
            if (!planbehUid) return;
            const ok = await toast.execute(
                async () => {
                    await link.mutateAsync({ dokumenter, planbehUid, napId });
                    return true;
                },
                { success: t('processings.documents.linkExisting.success', { count: dokumenter.length }) }
            );
            if (ok) setLinkExistingOpen(false);
        },
        [planbehUid, napId, toast, link, t]
    );

    // --- fjern fra behandlingen uten å slette dokumentet ---
    const [pendingUnlinkId, setPendingUnlinkId] = useState<string | null>(null);
    const pendingUnlink = useMemo(
        () => items.find(i => i.id === pendingUnlinkId) ?? null,
        [items, pendingUnlinkId]
    );
    const cancelUnlink = useCallback(() => setPendingUnlinkId(null), []);

    const confirmUnlink = useCallback(async () => {
        const dokument = data?.find(d => String(d.objectId) === pendingUnlinkId);
        if (!dokument || !planbehUid) return;
        const ok = await toast.execute(
            async () => {
                await unlink.mutateAsync({ dokument, planbehUid });
                return true;
            },
            { success: t('processings.documents.unlink.success') }
        );
        if (ok) setPendingUnlinkId(null);
    }, [data, pendingUnlinkId, planbehUid, toast, unlink, t]);

    // --- slett (#14579) ---
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const pendingDelete = useMemo(
        () => items.find(i => i.id === pendingDeleteId) ?? null,
        [items, pendingDeleteId]
    );

    const pendingDoc = useMemo(
        () => data?.find(d => String(d.objectId) === pendingDeleteId) ?? null,
        [data, pendingDeleteId]
    );

    // Bestemmelser på en vedtatt plan kan ikke frakobles (#14900). Samme regel
    // håndheves i use-casen; her brukes den til å vise forklaring i stedet for
    // bekreftelse.
    const deleteBlokkering = useMemo<FrakoblingBlokkering | null>(() => {
        if (!pendingDoc) return null;
        return finnFrakoblingBlokkering({ planstatus: plan?.planstatus, dokumenttype: pendingDoc.dokumenttype });
    }, [pendingDoc, plan]);

    // Før planen er lastet kan sperren for bestemmelser ikke avgjøres — da
    // holdes «Slett» deaktivert i stedet for å vise en bekreftelse som kan
    // snu til en sperre når planen kommer.
    const deleteAvventerPlan = pendingDoc?.dokumenttype === DOKUMENTTYPE_BESTEMMELSER && planLaster;

    const cancelDelete = useCallback(() => setPendingDeleteId(null), []);

    const confirmDelete = useCallback(async () => {
        if (!pendingDoc || deleteBlokkering || deleteAvventerPlan) return;
        const ok = await toast.execute(
            async () => {
                await remove.mutateAsync({ dokument: pendingDoc, napId });
                return true;
            },
            { success: t('documents.deleted') }
        );
        if (ok) setPendingDeleteId(null);
    }, [pendingDoc, deleteBlokkering, deleteAvventerPlan, napId, toast, remove, t]);

    const newDocument: NewDocumentState = {
        open: newOpen,
        values: newDraft,
        file: newFile,
        fieldErrors: newErrors,
        fileError,
        isSaving: create.isPending,
    };

    return {
        newDocument,
        openNew,
        closeNew,
        changeNew,
        changeNewFile,
        saveNew,
        pendingDelete,
        deleteBlokkering,
        deleteAvventerPlan,
        requestDelete: setPendingDeleteId,
        cancelDelete,
        confirmDelete,
        isDeleting: remove.isPending,
        linkExistingOpen,
        openLinkExisting,
        closeLinkExisting,
        confirmLinkExisting,
        isLinking: link.isPending,
        pendingUnlink,
        requestUnlink: setPendingUnlinkId,
        cancelUnlink,
        confirmUnlink,
        isUnlinking: unlink.isPending,
    };
}
